"use client";

import * as React from "react";
import { useState, useEffect } from "react";

import { VideoPlayer } from "@stadium/ui/src/components/video-player";

import LinkWithLang, {
  type LinkRouteDefaults,
} from "@stadium/shared/src/components/link-with-lang";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";
import Button from "@stadium/shared/src/components/shared/button";
import { type LinkWithLangType } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

interface BannerFeature {
  id: string;
  icon: any;
  iconAlt: string;
  text: string;
  video: string;
}

interface InteractiveFeaturesProps {
  bannerFeatures: BannerFeature[];
  ctaText?: string;
  ctaLinkType?: LinkWithLangType;
  ctaOpenInNewTab?: boolean;
  ctaOtherUrl?: string;
  ctaCategorySlug?: string;
  linkRouteDefaults?: LinkRouteDefaults;
  children?: React.ReactNode;
}

export default function InteractiveFeatures({
  bannerFeatures,
  ctaText,
  ctaLinkType,
  ctaOpenInNewTab,
  ctaOtherUrl,
  ctaCategorySlug,
  linkRouteDefaults,
  children,
}: Readonly<InteractiveFeaturesProps>) {
  const INDICATOR_TRANSITION_MS = 1000;
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [indicatorBaseTop, setIndicatorBaseTop] = useState(0);
  const [indicatorStep, setIndicatorStep] = useState(0);
  const [indicatorHeight, setIndicatorHeight] = useState(0);
  const featureItemRefs = React.useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const updateIndicatorMetrics = () => {
      const first = featureItemRefs.current[0];
      if (!first) return;

      const second = featureItemRefs.current[1];
      setIndicatorBaseTop(first.offsetTop);
      setIndicatorHeight(first.offsetHeight);
      setIndicatorStep(second ? second.offsetTop - first.offsetTop : first.offsetHeight);
    };

    updateIndicatorMetrics();
    globalThis.addEventListener("resize", updateIndicatorMetrics);
    return () => globalThis.removeEventListener("resize", updateIndicatorMetrics);
  }, [bannerFeatures.length]);

  const indicatorStyle = {
    "--feature-indicator-top": `${indicatorBaseTop + currentVideoIndex * indicatorStep}px`,
    "--feature-indicator-height": `${indicatorHeight}px`,
    "--feature-indicator-transition": `transform ${INDICATOR_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
  } as React.CSSProperties;

  return (
    <>
      {/* LEFT SECTION - Text Content */}
      <div className={styles.leftSection}>
        {children}

        {/* Features List */}
        <ul className={styles.featuresList} style={indicatorStyle}>
          <li className={styles.featureIndicator} aria-hidden />
          {bannerFeatures.map((feature, index) => (
            <li key={feature.id} ref={(el) => (featureItemRefs.current[index] = el)}>
              <Button
                type="button"
                styleType="no-style"
                className={`${styles.featureItem} ${
                  index === currentVideoIndex ? styles.featureItemActive : ""
                }`}
                onClick={() => setCurrentVideoIndex(index)}
                aria-label={`Show ${feature.text} video`}
                aria-pressed={index === currentVideoIndex}
              >
                <div className={styles.featureIcon}>
                  <Multimedia
                    type="image"
                    src={feature.icon?.url || feature.icon}
                    alt={feature.iconAlt}
                    className={styles.featureIconImage}
                  />
                </div>
                <span className={styles.featureText}>{feature.text}</span>
              </Button>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        {ctaText && ctaLinkType && (
          <div className={styles.ctaWrapper}>
            <LinkWithLang
              {...linkRouteDefaults}
              type={ctaLinkType}
              data={{
                code: ctaCategorySlug,
                url: ctaOtherUrl,
              }}
              target={ctaOpenInNewTab ? "_blank" : "_self"}
              className={styles.ctaLink}
            >
              {ctaText}
            </LinkWithLang>
            <span className={styles.ctaArrow} />
          </div>
        )}
      </div>

      {/* RIGHT SECTION - Product Video */}
      <div className={styles.rightSection}>
        <div className={styles.coloredBar}>
          <Multimedia
            src="https://cdn.bystadium.com/plasmic/static-slices/colored-bar.png"
            type="image"
            alt="colored-bar"
            className={styles.coloredBarImage}
          />
        </div>
        <div className={styles.videoWrapper}>
          {isMounted && (
            <VideoPlayer
              key={currentVideoIndex}
              url={bannerFeatures[currentVideoIndex].video}
              width="100%"
              height="100%"
              autoPlay
              controls={false}
              muted
              playsinline
              className={styles.video}
              onEnded={() => {
                const nextIndex = (currentVideoIndex + 1) % bannerFeatures.length;
                setCurrentVideoIndex(nextIndex);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}
